import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";

import INFO from "../data/user";
import SEO from "../data/seo";

import "./styles/contact.css";

const Contact = () => {
	const location = useLocation();
	const preselectedService = location.state?.selectedService;

	const [formData, setFormData] = useState({
		name: "",
		email: "",
		institution: "",
		services: preselectedService ? [preselectedService] : [],
		projectDescription: ""
	});

	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "contact");

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleServiceChange = (service) => {
		setFormData(prev => ({
			...prev,
			services: prev.services.includes(service)
				? prev.services.filter(s => s !== service)
				: [...prev.services, service]
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		// Validate that at least one service is selected
		if (formData.services.length === 0) {
			alert("Please select at least one service you're interested in.");
			return;
		}
		
		setIsSubmitting(true);
		
		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(formData),
			});
			
			const result = await response.json();
			
			if (response.ok) {
				// Show success modal instead of alert
				setShowSuccessModal(true);
				// Reset form
				setFormData({
					name: "",
					email: "",
					institution: "",
					services: [],
					projectDescription: ""
				});
			} else {
				throw new Error(result.error || 'Failed to send inquiry');
			}
		} catch (error) {
			console.error('Error submitting form:', error);
			alert("Sorry, there was an error sending your inquiry. Please try again or contact us directly at sbstatisticalconsulting@gmail.com");
		} finally {
			setIsSubmitting(false);
		}
	};

	const closeSuccessModal = () => {
		setShowSuccessModal(false);
	};

	const services = [
		"Statistical Analysis",
		"Sampling & Survey Design", 
		"Experimental Design & A/B Testing",
		"Operations Research & Optimization",
		"Other"
	];

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Contact | ${INFO.main.title}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta
					name="keywords"
					content={currentSEO.keywords.join(", ")}
				/>
			</Helmet>

			<div className="page-content">
				<NavBar active="contact" />
				<div className="content-wrapper">
					<div className="contact-logo-container">
						<div className="contact-logo">
							<Logo width={46} />
						</div>
					</div>

					<div className="contact-container">
						<div className="contact-header">
							<div className="contact-main-title">
								Are you in need of just in time statistical support?
							</div>
							
							<div className="contact-subtitle">
								Please fill out the form below and you can expect a reply from our team within <strong>24 hours</strong>.
							</div>
							
							<div className="contact-tagline">
								We deliver <strong>insights you can trust with impacts you can measure.</strong>
							</div>
						</div>

						<div className="contact-form-container">
							<form onSubmit={handleSubmit} className="contact-form">
								<div className="form-group">
									<label htmlFor="name" className="form-label">Name *</label>
									<input
										type="text"
										id="name"
										name="name"
										value={formData.name}
										onChange={handleInputChange}
										className="form-input"
										required
										disabled={isSubmitting}
									/>
								</div>

								<div className="form-group">
									<label htmlFor="email" className="form-label">Email *</label>
									<input
										type="email"
										id="email"
										name="email"
										value={formData.email}
										onChange={handleInputChange}
										className="form-input"
										required
										disabled={isSubmitting}
									/>
								</div>

								<div className="form-group">
									<label htmlFor="institution" className="form-label">Institution *</label>
									<input
										type="text"
										id="institution"
										name="institution"
										value={formData.institution}
										onChange={handleInputChange}
										className="form-input"
										required
										disabled={isSubmitting}
									/>
								</div>

								<div className="form-group">
									<label className="form-label">Services Interested In *</label>
									<div className="checkbox-group">
										{services.map((service) => (
											<div key={service} className="checkbox-item">
												<input
													type="checkbox"
													id={service}
													checked={formData.services.includes(service)}
													onChange={() => handleServiceChange(service)}
													className="checkbox-input"
													disabled={isSubmitting}
												/>
												<label htmlFor={service} className="checkbox-label">
													{service}
												</label>
											</div>
										))}
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="projectDescription" className="form-label">
										Optional Project Description
									</label>
									<textarea
										id="projectDescription"
										name="projectDescription"
										value={formData.projectDescription}
										onChange={handleInputChange}
										className="form-textarea"
										rows="4"
										placeholder="Please describe your project or statistical needs..."
										disabled={isSubmitting}
									/>
								</div>

								<button type="submit" className="form-submit-btn" disabled={isSubmitting}>
									{isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
								</button>
							</form>
						</div>
					</div>

					{/* Success Modal */}
					{showSuccessModal && (
						<div className="modal-overlay" onClick={closeSuccessModal}>
							<div className="modal-content" onClick={(e) => e.stopPropagation()}>
								<div className="modal-header">
									<h2 className="modal-title">Thank You!</h2>
								</div>
								<div className="modal-body">
									<p className="modal-message">
										Thank you for your inquiry! We'll get back to you within 24 hours.
									</p>
								</div>
								<div className="modal-footer">
									<button 
										className="modal-close-btn" 
										onClick={closeSuccessModal}
									>
										Close
									</button>
								</div>
							</div>
						</div>
					)}

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Contact;